import React, { useState, useEffect, useRef } from 'react';
import { InjectedRouter, RouterState, PlainRoute } from 'react-router';
import assertions from 'lib/assertions';
import EventTracking from 'lib/EventTracking';
import { pushCustomEvent } from 'lib/googleTagManager';
import { sendABTastyCustomEvent } from 'coaching/lib/abTasty';
import CoachingConstants from 'coaching/CoachingConstants';
import CoachingHeading from 'coaching/components/CoachingHeading';
import Button from 'components/Button';
import Copy from 'components/Copy';
import Row from 'components/Row';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
import Checkbox from 'components/Checkbox';
import Select from 'components/Select';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import Text from 'components/Text';
import { maybeCreateUserAndDevice } from 'coaching/actions/CoachingActions';
import ToastActions from 'actions/ToastActions';
import { PurchaserRole } from 'globalTypes';
import ConfirmEmailView from 'coaching/components/TutoringSelfServeView/ConfirmEmailView';
import {
  saveUnauthedPurchaserDetails,
  UnauthedPurchaserData,
} from 'lib/TutoringStateMachine/TutoringDataHelpers';

export type Props = {
  router: InjectedRouter & RouterState;
  route: PlainRoute;
  goToNextState: () => void;

  restoredProps: {
    unauthedPurchaserDetails: UnauthedPurchaserData | null;
  };
};

export type TutoringSelfServeViewGetStartedUnauthenticatedState = {
  selectedRole?: PurchaserRole;
  fullName: string;
  emailAddress?: string;
  checkPolicies: boolean;
};

const eventPrefix = 'coaching.self_schedule_purchase.plans_summary';

function trackContinueClick(data: Record<string, any>): void {
  EventTracking.trackKey(`${eventPrefix}.continue.click`, {
    data: JSON.stringify(data),
  });
}

export default function TutoringSelfServeViewGetStartedUnauthenticated(props: Props) {
  const stateLoadingeRef = useRef(false);
  const tutoringRoleOptions = CoachingConstants.TUTORING_STREAMLINED_TUTORING_ROLES.map(r => ({
    label: r.label,
    value: r.role,
  }));

  const [state, setState] = useState<TutoringSelfServeViewGetStartedUnauthenticatedState>(() => {
    const restoredUserInfo = props.restoredProps?.unauthedPurchaserDetails;
    return {
      selectedRole: restoredUserInfo?.purchaserRole ?? tutoringRoleOptions[0].value,
      fullName: restoredUserInfo?.fullName || '',
      emailAddress: restoredUserInfo?.email || '',
      checkPolicies: restoredUserInfo?.checkPolicies || false,
    };
  });
  const [loading, setLoading] = useState(false);
  const [needsConfirmEmail, setNeedsConfirmEmail] = useState(false);
  const isContinueButtonDisabled =
    loading ||
    !state.fullName ||
    !state.emailAddress ||
    !state.selectedRole ||
    !state.checkPolicies;

  stateLoadingeRef.current = loading;
  useEffect(() => {
    sendABTastyCustomEvent('tutoring_start');
    pushCustomEvent('tutoring_welcome');
    EventTracking.trackKey(`${eventPrefix}.view`);
    props.router.setRouteLeaveHook(props.route, () => {
      return !stateLoadingeRef.current;
    });
  }, []);

  const { goToNextState } = props;
  const { emailAddress, fullName, selectedRole } = state;

  const onDone = () => {
    saveUnauthedPurchaserDetails({
      purchaserRole: state.selectedRole!,
      email: state.emailAddress,
      fullName: state.fullName,
      checkPolicies: state.checkPolicies,
    });
    goToNextState();
  };

  async function onContinue() {
    setLoading(true);
    const response = await maybeCreateUserAndDevice({
      name: state.fullName,
      email: state.emailAddress!, // We validate that an email exists before showing this screen
      role: state.selectedRole!, // We previously validate that some role was selected
    });
    setLoading(false);

    if (response.error) {
      const msg = 'There was a problem. Please try again.';
      ToastActions.showErrorToast(msg);
      return;
    }

    if (response.deviceConfirmationRequired) {
      setNeedsConfirmEmail(true);
    } else {
      onDone();
    }

    trackContinueClick({
      email: emailAddress,
      name: fullName,
      role: selectedRole,
      existing_user: response.deviceConfirmationRequired, // only correct when force device confirmation is toggled off
    });
  }

  if (needsConfirmEmail) {
    return (
      <ConfirmEmailView
        email={state.emailAddress!}
        setLoading={setLoading}
        onConfirmationDone={onDone}
        // Just render the get started screen again
        onChangeEmail={() => setNeedsConfirmEmail(false)}
      />
    );
  }

  return (
    <React.Fragment>
      <CoachingHeading data-test="get-started-heading">Get started</CoachingHeading>
      <Row size="small">
        <Copy align="left" size="xsmall">
          Sign up and schedule a FREE 40 minute tutoring session.
        </Copy>
      </Row>
      <Row size="large">
        <FormElement
          data-test="role-dropdown"
          onValidChange={e => {
            setState({ ...state, selectedRole: e });
          }}
          label="Who needs tutoring?"
          element={Select}
          elementProps={{
            options: tutoringRoleOptions,
            value: state.selectedRole,
          }}
        />
        <FormElement
          data-test="full-name-field"
          onValidChange={e => setState({ ...state, fullName: e.target.value })}
          onInvalidChange={() => setState({ ...state, fullName: '' })}
          validate={e => assertions.firstAndLastName(e.target.value)}
          label="Your full name"
          element={Input}
          elementProps={{
            name: 'full-name-field',
            defaultValue: state.fullName,
            placeholder: 'First and last name',
          }}
          trimWhitespace
        />
        <FormElement
          data-test="email-address-field"
          onValidChange={e => setState({ ...state, emailAddress: e.target.value })}
          onInvalidChange={() => setState({ ...state, emailAddress: '' })}
          validate={e => assertions.email(e.target.value)}
          label="Your email address"
          element={Input}
          elementProps={{
            name: 'email-address-field',
            defaultValue: state.emailAddress,
            placeholder: 'Email address',
          }}
          trimWhitespace
        />
        <Row size="xxxsmall">
          <Text align="left">
            Please read and agree to Remind’s{' '}
            <Link href={LinkConstants.TERMS_OF_SERVICE} target="_blank">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link href={LinkConstants.PRIVACY_POLICY} target="_blank">
              Privacy Policy
            </Link>{' '}
            before continuing.
          </Text>
        </Row>
        <Checkbox
          data-test="check-policy-field"
          checked={state.checkPolicies}
          onChange={e => setState({ ...state, checkPolicies: e.target.checked })}
          label={`I’ve read and agree`}
        />
      </Row>
      <Button
        fullAtBreakpoint="480"
        data-test="get-started-continue-button"
        color="koolaid"
        loading={loading}
        onClick={onContinue}
        disabled={isContinueButtonDisabled}>
        Continue
      </Button>
    </React.Fragment>
  );
}
